// ────────────────────────────────────────────────────────────────────────────
// Sample document data for the builder preview.
//
// Deliberately awkward rather than tidy: mixed VAT rates (so the breakdown
// has something to show), a long description that has to wrap, a line with no
// order number, and figures wide enough to expose a too-narrow money column.
// A preview built on friendly data hides exactly the layout faults the
// operator needs to see before they make a template the default.
//
// The organisation block is replaced with the operator's own org once the
// builder has it loaded — only the customer, lines and totals stay synthetic.
// ────────────────────────────────────────────────────────────────────────────
import {
  buildVatBands,
  DOC_TYPE_PRINT_NAME,
  type DocumentData,
  type DocumentLine,
  type DocumentLineContext,
} from './documentData';
import type { DocumentType } from './types';

/** Order context, attached to every line of its order exactly as generation does. */
const ORD_10241: DocumentLineContext = {
  collectionDate: '2026-08-12',
  deliveryDate: '2026-08-13',
  collectionPlace: 'Dunnes Stores, Limerick, IE',
  deliveryPlace: 'Arla Foods UK, Leeds, GB',
  references: ['CUST-REF-88213', 'JOB-4471', null, null],
  trailer: 'TR-118',
  container: 'MSCU-4471820',
  cargoProduct: 'Chilled dairy',
  cargoTemperatureC: 2,
};

const ORD_10258: DocumentLineContext = {
  collectionDate: '2026-08-14',
  deliveryDate: '2026-08-16',
  collectionPlace: 'Rosslare Europort, IE',
  deliveryPlace: 'Cherbourg, FR',
  references: ['PO-4471', null, null, null],
  trailer: 'TR-204',
  container: null,
  cargoProduct: 'Palletised goods',
  cargoTemperatureC: null,
};

/** An order billed for a surcharge only — its transport charge is not on this invoice. */
const ORD_10262: DocumentLineContext = {
  collectionDate: '2026-08-17',
  deliveryDate: '2026-08-18',
  collectionPlace: 'Ballymount, IE',
  deliveryPlace: 'Cork, IE',
  references: ['JOB-5120', null, null, null],
  trailer: 'TR-119',
  container: null,
  cargoProduct: 'Ambient goods',
  cargoTemperatureC: null,
};

/**
 * Shaped like a real haulage invoice, and deliberately covering the four cases
 * the line table has to get right:
 *
 *  1. An order whose transport charge carries the route, with its surcharges
 *     underneath repeating none of the order's detail.
 *  2. A second order, so the grouping resets.
 *  3. An order billed for a surcharge ONLY — the date and reference still print
 *     because they belong to the order, but nothing claims the route, so the
 *     charge keeps its own description.
 *  4. An ad-hoc charge with no order at all.
 *
 * Also awkward on purpose: mixed VAT rates, and a description long enough to
 * wrap in a narrow column.
 */
const SAMPLE_LINES: DocumentLine[] = [
  {
    id: 'sample-1',
    description: 'Full load haulage — 24t refrigerated',
    orderId: 'o-10241',
    orderNumber: 'ORD-10241',
    reference: 'CUST-REF-88213',
    isTransportCharge: true,
    context: ORD_10241,
    quantity: 1,
    unitPrice: 780,
    net: 780,
    taxRatePct: 23,
    taxAmount: 179.4,
    gross: 959.4,
  },
  {
    id: 'sample-2',
    description: 'Fuel surcharge 8.5%',
    orderId: 'o-10241',
    orderNumber: 'ORD-10241',
    reference: null,
    context: ORD_10241,
    quantity: null,
    unitPrice: null,
    net: 66.3,
    taxRatePct: 23,
    taxAmount: 15.25,
    gross: 81.55,
  },
  {
    id: 'sample-3',
    description: 'Waiting time (2.5 hrs beyond free period)',
    orderId: 'o-10241',
    orderNumber: 'ORD-10241',
    reference: null,
    context: ORD_10241,
    quantity: 2.5,
    unitPrice: 45,
    net: 112.5,
    taxRatePct: 23,
    taxAmount: 25.88,
    gross: 138.38,
  },
  {
    id: 'sample-4',
    description: 'Ferry crossing — accompanied unit',
    orderId: 'o-10258',
    orderNumber: 'ORD-10258',
    reference: 'PO-4471',
    isTransportCharge: true,
    context: ORD_10258,
    quantity: 1,
    unitPrice: 1240,
    net: 1240,
    taxRatePct: 0,
    taxAmount: 0,
    gross: 1240,
  },
  {
    id: 'sample-5',
    description: 'Customs entry fee',
    orderId: 'o-10258',
    orderNumber: 'ORD-10258',
    reference: null,
    context: ORD_10258,
    quantity: null,
    unitPrice: null,
    net: 45,
    taxRatePct: 23,
    taxAmount: 10.35,
    gross: 55.35,
  },
  {
    // Case 3: no transport charge on this order, so no route — the surcharge
    // keeps its own description while the date and reference still print.
    id: 'sample-6',
    description: 'Fuel surcharge 8.5%',
    orderId: 'o-10262',
    orderNumber: 'ORD-10262',
    reference: null,
    context: ORD_10262,
    quantity: null,
    unitPrice: null,
    net: 58.65,
    taxRatePct: 23,
    taxAmount: 13.49,
    gross: 72.14,
  },
  {
    id: 'sample-7',
    description: 'Pallet storage, 14 days',
    orderId: null,
    orderNumber: null,
    reference: null,
    quantity: 14,
    unitPrice: 8.5,
    net: 119,
    taxRatePct: 13.5,
    taxAmount: 16.07,
    gross: 135.07,
  },
];

/** Build the preview payload for a document kind. */
/** Sample reference per kind, matching each kind's default sequence prefix. */
const SAMPLE_NUMBER: Record<DocumentType, string> = {
  Invoice: 'INV-004192',
  CreditNote: 'CN-000418',
  Proforma: 'PRO-000042',
};

export function buildSampleData(docType: DocumentType): DocumentData {
  const subtotal = SAMPLE_LINES.reduce((sum, l) => sum + l.net, 0);
  const vatTotal = SAMPLE_LINES.reduce((sum, l) => sum + l.taxAmount, 0);
  return {
    organisation: {
      name: 'Sample Haulage Ltd',
      addressLines: ['Unit 4, Northgate Business Park', 'Ballymount', 'Dublin 12', 'Ireland'],
      taxNumber: 'IE1234567T',
      registrationNumber: '512334',
      phone: '+353 1 555 0100',
      email: 'accounts@samplehaulage.ie',
      website: 'www.samplehaulage.ie',
      logoUrl: null,
    },
    customer: {
      name: 'Kilkenny Cold Chain Distribution',
      code: 'KCCD-004',
      // One comma-separated string, exactly as the invoice snapshots it.
      address: 'Hebron Industrial Estate, Hebron Road, Kilkenny, R95 XV21, Ireland',
      taxNumber: 'IE9988776W',
      contactName: 'Accounts Payable',
      phone: '+353 56 555 0142',
      email: 'ap@kccd.ie',
    },
    doc: {
      // Driven off the shared print-name map, not a two-way branch — a third
      // document kind previously fell through to "Invoice" and made the pro
      // forma preview claim to be one.
      typeName: DOC_TYPE_PRINT_NAME[docType],
      docType,
      number: SAMPLE_NUMBER[docType],
      issueDate: '2026-08-18T00:00:00Z',
      dueDate: '2026-09-17T00:00:00Z',
      poNumber: 'PO-4471',
      reference: 'Week 34 consolidated',
      paymentTerms: '30 days from invoice date',
      currencyIsoCode: 'EUR',
    },
    lines: SAMPLE_LINES,
    totals: {
      subtotal,
      vatTotal,
      total: subtotal + vatTotal,
      vatBands: buildVatBands(SAMPLE_LINES),
    },
    bank: {
      accountName: 'Sample Haulage Ltd',
      bankName: 'Bank of Ireland, Ballymount',
      iban: 'IE29 BOFI 9012 3412 3456 78',
      bic: 'BOFIIE2D',
      sortCode: '90-12-34',
      accountNumber: '12345678',
    },
    message: 'Thank you for your business.',
  };
}
